import { AgendaItemsTable } from "@/components/agendaItems/AgendaItemsTable";
import { CreateAgendaItemDialog } from "@/components/agendaItems/CreateAgendaItemDialog";
import { ExportAgendaItemDialog } from "@/components/agendaItems/ExportAgendaItemDialog";
import { RoleProtected } from "@/components/common/RoleProtected";
import { PageHeader } from "@/components/page";
import { useGetTenantByFriendly } from "@/hooks/queryHooks/tenantHooks";
import { useRoleProtected } from "@/hooks/useRoleProtected";
import { useUserStore } from "@/stores/userStore";
import type { AgendaItem } from "@repo/types";
import { DataTableWrapper } from "@repo/ui/components/Base/DataTable";
import {
  dataTableReverseContext,
  getAllTablesData,
  getAllTableVisibleFields,
} from "@repo/ui/components/Base/dataTableReverseContext";
import { useConstComponent } from "@repo/ui/components/Base/hooks/useNestedComponent";
import { useParams } from "react-router-dom";

export const AgendaItemsPage = () => {
  const { tenantId } = useParams();
  const activeTeam = useUserStore((s) => s.activeTeam);
  useRoleProtected("submit");
  const { loaded: tenantLoaded, data: tenantData } = useGetTenantByFriendly(
    tenantId ?? ""
  );

  const tableContext = dataTableReverseContext.useAsParent();
  const Provider = useConstComponent(tableContext.Provider);
  const getAgendaItems = () => getAllTablesData<AgendaItem>(tableContext);
  const getSelectedFields = () => getAllTableVisibleFields(tableContext);

  return (
    <div className="flex flex-col flex-grow">
      <PageHeader
        title={
          tenantData?.name
            ? `${tenantData.name}'s Agenda Items`
            : "Agenda Items"
        }
      >
        {activeTeam === "org" && (
          <p className="text-red-500 w-0 flex-grow-[3] mr-8 text-balance text-right">
            Without a team selected, you can only edit items you created. You
            must select a team to be able to create agenda items from here, and
            access linked sections and documents on items.
          </p>
        )}
        <div className="flex flex-row gap-3">
          <RoleProtected minRole="edit">
            <ExportAgendaItemDialog
              getAgendaItems={getAgendaItems}
              getPreselectedFields={getSelectedFields}
            />
          </RoleProtected>
          {tenantData?.id && (
            <RoleProtected minRole="submit">
              <CreateAgendaItemDialog
                tenantId={tenantData.id}
                disabled={activeTeam === "org"}
              />
            </RoleProtected>
          )}
        </div>
      </PageHeader>
      {!tenantLoaded && <div>Loading ...</div>}

      {tenantLoaded && tenantData?.id && (
        <DataTableWrapper>
          <Provider>
            <AgendaItemsTable tenantId={tenantData?.id} />
          </Provider>
        </DataTableWrapper>
      )}
    </div>
  );
};
